<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import type { CashDrawer } from '../../../../interfaces';
import type { UpdatePeopleOptInsResponse } from '@PeopleModule';
import { useAToast, useADialog } from '@Atoms';
import { toCents } from '@Helpers';
import { useSessionStore } from '@Stores';
import { ROLES } from '@AuthModule';
import { useForm } from '@FormModule';
import { GetPeopleOptInsService } from '@PeopleModule';
import { useCartSteps, useStripeCardConfirm } from '../../../../composables';
import { CART_STEPS, CUSTOM_OUTCOME_TYPE, PAYMENT_METHODS } from '../../../../enums';
import { CART_TYPES } from '../../../../enums';
import {
  CompleteOutcomeService,
  CompleteSplitPaymentService,
  PostCitationPaymentService,
  ProcessStripePaymentService,
  UpdatePaymentTypeService,
} from '../../../../services';
import useShoppingCartStore from '../../../../store';
import getSanitizedPayload from './getSanitizedPayload.helper';
import useFields from './useFields';

const emits = defineEmits<{
  onStepUpdate: [CART_STEPS];
}>();

const { can } = useSessionStore();
const {
  animals,
  cartData,
  completeOutcomeRoute,
  confirmingCartWithTotalZero,
  hasCart,
  isAdoption,
  isCitation,
  isDonation,
  isProduct,
  isReturnToOwner,
  loading,
  outcomeType,
} = storeToRefs(useShoppingCartStore());
const { fetchCashDrawerOptions, outcomeCompletedCartReset } = useShoppingCartStore();
const { shelterProfile } = storeToRefs(useSessionStore());

const { activeModal, activeModalProps, dialogVisibility, SYSTEM_MODALS } = useADialog();
const { currentStep, previousStep } = useCartSteps();
const { confirmCardPayment, getStripeCardModel, setStripeCardError } = useStripeCardConfirm();
const {
  clearPaymentMethod,
  currentSchemaModels,
  fieldsSchema,
  formError,
  paymentMethodModel,
  resetFieldSchema,
  setForm,
} = useFields();
const { fieldsValidation } = useForm();
const { notify, TOAST_TYPES } = useAToast();
const { push } = useRouter();

// NOTE :: The api has no error code for a dead cart session — it answers 400 with a sentence, so
// the copy is the only signal we get. All four guards now send the same one ("Your checkout session
// has expired.") from OutcomeRepository, ShoppingCartController and DonationPaymentRepository. The
// match stays loose on purpose: it also catches the older "Cart session expired;" wording, which is
// still what a server running the previous release answers.
const CART_SESSION_EXPIRED_PATTERN = /session (has )?expired/i;
const CART_SESSION_EXPIRED_MESSAGE = 'cart.toast.cartSessionExpired.error.message';
const TOAST_CART_SESSION_EXPIRED = {
  message: CART_SESSION_EXPIRED_MESSAGE,
  title: 'cart.toast.cartSessionExpired.error.title',
  type: TOAST_TYPES.ERROR,
};
const TOAST_ERROR = {
  message: 'errors.generics.unknownIssue.message',
  title: 'errors.generics.unknownIssue.title',
  type: TOAST_TYPES.ERROR,
};
const TOAST_PAYMENT_METHOD_ERROR = {
  message: 'cart.toast.paymentMethodUpdate.error.message',
  title: 'cart.toast.paymentMethodUpdate.error.title',
  type: TOAST_TYPES.ERROR,
};

const isSubmitting = ref(false);
const paymentTypeSaving = ref(false);
const requestErrorMessage = ref('');
const sendDocupet = ref(false);
const sendHills = ref(false);
const sendAdoptAPet = ref(false);

const OUTCOME_TYPES_FOR_DOCUPET = ['adopted', 'return_to_owner', 'clinic_out', 'service_out'];
const showDocupet = computed(
  () =>
    isOutcome.value && !!shelterProfile.value?.docupet_enabled && OUTCOME_TYPES_FOR_DOCUPET.includes(outcomeType.value)
);
const showHills = computed(
  () => isOutcome.value && !!shelterProfile.value?.hills_report && outcomeType.value === 'adopted'
);
const showAdoptAPetOptIn = computed(
  () => isOutcome.value && !!shelterProfile.value?.adopt_a_pet_opt_in && outcomeType.value === 'adopted'
);
const showThirdPartyIntegrations = computed(() => showDocupet.value || showHills.value || showAdoptAPetOptIn.value);

const isRetroactivePayment = computed(() => cartData.value?.paid_date);
const isOutcome = computed(() => cartData.value?.cart_type === CART_TYPES.OUTCOME);
const preAdoptFlags = computed(() => ({
  enablePrePaymentForRtos: isReturnToOwner.value && shelterProfile.value?.enable_pre_payment_for_rtos,
  enablePreAdoptForAdoptions: isAdoption.value && shelterProfile.value?.enable_pre_adopt_for_adoptions,
}));
const showPrePaymentOption = computed(
  () =>
    isOutcome.value && (preAdoptFlags.value.enablePreAdoptForAdoptions || preAdoptFlags.value.enablePrePaymentForRtos)
);
const shelterHasOffsiteAdoption = computed(
  () =>
    shelterProfile.value?.has_offsite_adoption &&
    can(ROLES.OFFSITE_ANIMAL) &&
    isAdoption.value &&
    !isRetroactivePayment.value
);
const disableOffsiteButton = computed(
  () =>
    loading.value ||
    [PAYMENT_METHODS.CREDIT, PAYMENT_METHODS.SPLIT_PAYMENT].includes(paymentMethodModel.value as PAYMENT_METHODS)
);
const cartItemType = computed(() =>
  //prettier-ignore
  isCitation.value ? 'citation' :
  isProduct.value ? 'product' :
  isDonation.value ? 'donation' :
  undefined
);

const preAdoptLabel = computed(() =>
  // prettier-ignore
  !isOutcome.value ? ''
  :isAdoption.value ? 'cart.label.preAdopt'
  : isReturnToOwner.value ? 'cart.label.prePayment'
  : ''
);

watch(confirmingCartWithTotalZero, () => onConfirmCardWithTotalZero());
watch(paymentMethodModel, () => updatePaymentType());

onMounted(async () => await onStepInit());

// HELPERS
function setRequestErrorMessage(error: any) {
  requestErrorMessage.value = error?.response?.data?.error || 'cart.toast.defaultError.error.message';
}

function getCashDrawer(): CashDrawer | undefined {
  return shelterProfile.value?.cash_drawer
    ? fieldsSchema.value.find(({ name }) => name === 'cash_drawer')?.model
    : undefined;
}

function setPaymentFieldsDisabled(disabled: boolean) {
  fieldsSchema.value.forEach((field) => (field.state.disabled = disabled));
}

// SERVICES
// NOTE :: The submit is gated on this call. The server recalculates the credit-card fee from the
// session cart's payment_type, so until this lands the totals on screen are not the totals that
// will be charged. Letting the staff submit in that window shows one amount and charges another.
function updatePaymentType() {
  if (!paymentMethodModel.value) return;

  requestErrorMessage.value = '';
  paymentTypeSaving.value = true;

  // NOTE :: The step locks until this lands. Picking a second method mid-flight sent two requests,
  // and whichever answered last won cartData — so the screen could show cash totals while the
  // payload said credit. Waiting is the point.
  setPaymentFieldsDisabled(true);

  const PAYLOAD = {
    payment_type: paymentMethodModel.value,
  };

  return UpdatePaymentTypeService(PAYLOAD)
    .then(({ data }) => (cartData.value = data))
    .catch((error) => onPaymentTypeError(error))
    .finally(() => {
      paymentTypeSaving.value = false;
      setPaymentFieldsDisabled(false);
    });
}

// EVENTS
async function onStepInit() {
  loading.value = true;

  await fetchCashDrawerOptions();

  currentStep.value = CART_STEPS.PAYMENT_METHOD;
  resetFieldSchema();
  setForm();

  loading.value = false;

  fetchPersonOptIns();
}

// NOTE :: The partner boxes start from the consent saved on the person, not always unticked. A
// person the cart has not saved yet has no consent, so the api answers an empty list.
function fetchPersonOptIns() {
  const PERSON_ID = cartData.value?.people?.person_id;
  const shouldSkipFetch = !showThirdPartyIntegrations.value || !PERSON_ID;

  if (shouldSkipFetch) return;

  GetPeopleOptInsService(PERSON_ID)
    .then(onPersonOptInsLoad)
    .catch(() => notify(TOAST_ERROR));
}

function onPersonOptInsLoad(response: Pick<UpdatePeopleOptInsResponse, 'opt_ins'>) {
  sendDocupet.value = response.opt_ins.includes('docupet');
  sendHills.value = response.opt_ins.includes('hills');
  sendAdoptAPet.value = response.opt_ins.includes('adopt_a_pet');
}

// NOTE :: Clearing the field is the fix, not a nicety. The save failed, so the cart still holds the
// old payment type while the screen shows the new one — the staff would charge cash totals on a
// credit cart. With nothing selected the required-field check blocks the submit until a pick lands.
//
// The expired-session branch matters just as much: picking again is the one thing that cannot work
// once the cart session is gone, so telling the staff to do that leaves them stuck in a loop.
function onPaymentTypeError(error: any) {
  const isCartSessionExpired = CART_SESSION_EXPIRED_PATTERN.test(error?.response?.data?.error ?? '');

  clearPaymentMethod();

  if (isCartSessionExpired) {
    requestErrorMessage.value = CART_SESSION_EXPIRED_MESSAGE;
    return notify(TOAST_CART_SESSION_EXPIRED);
  }

  requestErrorMessage.value = 'cart.toast.paymentMethodUpdate.error.message';
  notify(TOAST_PAYMENT_METHOD_ERROR);
}

function onNextStep(nextStep: 'offsite' | 'preAdopt' | null) {
  const isStepBusy = loading.value || isSubmitting.value || paymentTypeSaving.value;
  if (isStepBusy) return;

  const hasErrors = fieldsValidation(fieldsSchema.value, currentSchemaModels.value);
  if (hasErrors) return (formError.value = true);

  formError.value = false;

  const animalsBaseRtoSanitezed = animals.value.map((animal: any) => {
    const needsAdjustments = animal.base_rto_fee && typeof animal.base_rto_fee === 'object';

    if (!needsAdjustments) return animal;

    return {
      ...animal,
      base_rto_fee: animal.base_rto_fee.amount.toString() || 0,
      outcome: {
        ...animal.outcome,
        fee: animal.base_rto_fee.amount.toString(),
      },
    };
  });

  const isSplitPayment = paymentMethodModel.value === PAYMENT_METHODS.SPLIT_PAYMENT;
  const isCreditCard = paymentMethodModel.value === PAYMENT_METHODS.CREDIT;
  const isOffsite = nextStep === 'offsite';
  const isPreAdopt = nextStep === 'preAdopt';

  const payload = {
    ...cartData.value,
    ...getSanitizedPayload(currentSchemaModels.value, shelterProfile.value, isSplitPayment),
    payment_type: paymentMethodModel.value, // Use UI model directly to avoid race condition with async updatePaymentType()
    split_payment: isSplitPayment,
    animals: animalsBaseRtoSanitezed,
    send_docupet: showDocupet.value ? sendDocupet.value : undefined,
    send_hills: showHills.value ? sendHills.value : undefined,
    send_adopt_a_pet: showAdoptAPetOptIn.value ? sendAdoptAPet.value : undefined,
  };

  const isCash = paymentMethodModel.value === PAYMENT_METHODS.CASH;
  const hasAmountCollected = payload.cash_amount_collected != null;
  if (isCash && hasAmountCollected) {
    const totalCharge = cartData.value?.total_charge ?? cartData.value?.total ?? 0;
    const changeCents = toCents(payload.cash_amount_collected) - toCents(totalCharge);

    payload.cash_change_due = changeCents / 100;
  }

  if (isOffsite) return onOffSitePending(payload);
  if (isPreAdopt) return onPreAdopt(payload);

  const cartWithTotalZero = !Number(cartData.value?.total) && hasCart.value;
  if (cartWithTotalZero) {
    // NOTE :: A card cannot charge $0. Stripe refuses a charge under 50 cents. Say so here.
    // Do not open the zero-total confirm and let the payment call fail.
    if (isCreditCard || isSplitPayment) {
      requestErrorMessage.value = 'cart.title.paymentMethodRequiresMinimumCreditCard';
      return;
    }

    activeModal.value = SYSTEM_MODALS.CART_ZERO_TOTAL_CONFIRMATION;
    activeModalProps.value = {
      cartPayload: payload,
    };
    dialogVisibility.value = true;
    loading.value = false;
    return;
  }

  submitCartPayment(payload);
}

// NOTE :: Used by the submit button and the zero-total confirm. When a card is charged (credit
// card, or a split that includes a card amount) the api first builds a PaymentIntent and the
// browser confirms it. If the bank asks for a 3D Secure check, it pops over the loading dialog.
// Only then does the finish call run, with `payment_intent_id` instead of a token. `loading`
// stays on for the whole chain.
function submitCartPayment(payload: any) {
  const isSplitPayment = payload.payment_type === PAYMENT_METHODS.SPLIT_PAYMENT;
  const isCreditCard = payload.payment_type === PAYMENT_METHODS.CREDIT;
  const splitCardCents = toCents(payload.split_payment_details?.credit_card_amount ?? 0);
  const needsCardConfirm = isCreditCard || (isSplitPayment && splitCardCents > 0);

  loading.value = true;
  isSubmitting.value = true;
  requestErrorMessage.value = '';

  // prettier-ignore
  const TargetService = isSplitPayment ? CompleteSplitPaymentService
    : isCitation.value ? PostCitationPaymentService
    : isCreditCard ? ProcessStripePaymentService
    : CompleteOutcomeService;

  activeModal.value = SYSTEM_MODALS.COMPLETE_OUTCOME_LOADING_MESSAGE;
  dialogVisibility.value = true;

  const stripeCard = getStripeCardModel(fieldsSchema.value);
  const confirmation = needsCardConfirm
    ? confirmCardPayment(payload, stripeCard)
    : Promise.resolve({ error: undefined, payment_intent_id: undefined });

  confirmation
    .then(async ({ error, payment_intent_id }) => {
      if (error) return onCardConfirmError(error);

      await TargetService({ ...payload, payment_intent_id });

      if (completeOutcomeRoute.value) await push(completeOutcomeRoute.value).then(() => outcomeCompletedCartReset());
    })
    .catch(async (error) => {
      // NOTE :: Duplicate completion error handling
      if (error?.response?.status === 409 && completeOutcomeRoute.value) {
        await push(completeOutcomeRoute.value);
        outcomeCompletedCartReset();
        return;
      }

      notify(TOAST_ERROR);
      setRequestErrorMessage(error);
    })
    .finally(() => {
      loading.value = false;
      isSubmitting.value = false;
      dialogVisibility.value = false;
    });
}

// NOTE :: A card decline is not a system error. Do not show a toast or a box at the step level.
// Show Stripe's sentence once, under the card. The "review the fields above" note by the buttons
// says where to look. A split card does not leave an error on the field: a decline must not block
// the next submit. Pass the note's own text so it still shows. Submit again on the same card.
// Changing only the name is enough. The number does not have to be retyped. An incomplete card
// number still blocks submit.
function onCardConfirmError(message: string) {
  setStripeCardError(getStripeCardModel(fieldsSchema.value), message);
  formError.value = 'components.forms.errors.reviewForm';
}

function onOffSitePending(payload: any) {
  activeModal.value = SYSTEM_MODALS.CART_PRE_PAYMENT;

  const { cart_id, paid_date } = cartData.value;

  activeModalProps.value = {
    cardReaderLastFour: payload.last_four_digits,
    cartId: cart_id.$oid,
    cashDrawer: getCashDrawer(),
    customOutcomeType: CUSTOM_OUTCOME_TYPE.OFFSITE,
    isCompleteOutcome: false,
    isOffsite: true,
    paidDate: paid_date,
    paymentType: payload.payment_type,
    ...payload,
  };

  dialogVisibility.value = true;
}

function onPreAdopt(payload: any) {
  activeModal.value = SYSTEM_MODALS.CART_PRE_PAYMENT;

  const { cart_id, paid_date } = cartData.value;

  activeModalProps.value = {
    cardReaderLastFour: payload?.last_four_digits,
    checkNumber: payload?.check_number,
    cartId: cart_id.$oid || cart_id,
    cashDrawer: getCashDrawer(),
    customOutcomeType: CUSTOM_OUTCOME_TYPE.PRE_ADOPT,
    isCompleteOutcome: false,
    isOffsite: false,
    isRto: isReturnToOwner,
    paidDate: paid_date,
    paymentType: payload.payment_type,
    stripeCard: getStripeCardModel(fieldsSchema.value), // NOTE :: the modal confirms the card and removes this before the api call
    ...payload,
  };

  dialogVisibility.value = true;
}

function onConfirmCardWithTotalZero() {
  if (loading.value || isSubmitting.value) return;

  const flowIssue = !confirmingCartWithTotalZero.value || !activeModalProps.value?.cartPayload;
  if (flowIssue) {
    requestErrorMessage.value = 'Something went wrong while confirming you submission. Please try again.';
    return;
  }

  submitCartPayment(activeModalProps.value.cartPayload);
}
</script>

<template>
  <div class="flex h-fit flex-col gap-6">
    <main class="flex-1">
      <FormTemplate
        class="max-w-[500px]"
        :formError
        :schema="fieldsSchema"
      />

      <!-- NOTE :: This sits right under the Paid Date field. That field exists only on a donation,
      so the note sits next to the date it talks about. The target exists only while the form is
      mounted. `defer` waits for it. -->
      <Teleport
        v-if="isDonation && paymentMethodModel === PAYMENT_METHODS.CREDIT"
        defer
        to="[data-field='paid_date']"
      >
        <Message
          class="mt-1"
          pt:text="text-sm"
          severity="info"
          :closable="false"
        >
          {{ $t('cart.label.updatePaidDateForDonation') }}
        </Message>
      </Teleport>

      <div v-if="paymentMethodModel && paymentMethodModel !== PAYMENT_METHODS.SPLIT_PAYMENT">
        <Divider
          class="before:border-t-2"
          type="dashed"
        />

        <div class="flex max-w-[500px] flex-col justify-between gap-8">
          <AShoppingCartItemCost
            v-if="cartData"
            totalOnly
            :cartData
            :cartItemType
            :item="cartData"
          />
        </div>
      </div>

      <!-- Third Party Integrations -->
      <div
        v-if="showThirdPartyIntegrations"
        class="flex max-w-[500px] flex-col gap-3 pt-4"
      >
        <Divider
          class="before:border-t-2"
          type="dashed"
        />
        <p class="font-semibold">
          {{ $t('cart.label.thirdPartyIntegrations') }}
        </p>
        <p class="text-sm text-slate-500">
          {{ $t('cart.label.thirdPartyIntegrationsSubtitle') }}
        </p>
        <div class="flex flex-col gap-2">
          <div
            v-if="showDocupet"
            class="flex cursor-pointer items-start gap-3 rounded border border-gray-200 p-3 transition-colors hover:bg-gray-50"
            @click="sendDocupet = !sendDocupet"
          >
            <Checkbox
              :binary="true"
              :inputId="'docupet-checkbox'"
              :modelValue="sendDocupet"
              @click.stop
              @update:modelValue="sendDocupet = $event"
            />
            <label
              class="flex-1 cursor-pointer"
              :for="'docupet-checkbox'"
            >
              <span class="font-semibold">{{ $t('cart.label.docupet') }}</span>

              <p class="mt-0.5 text-sm text-slate-500">
                {{ $t('cart.label.docupetDescription') }}
              </p>
            </label>
          </div>
          <div
            v-if="showHills"
            class="flex cursor-pointer items-start gap-3 rounded border border-gray-200 p-3 transition-colors hover:bg-gray-50"
            @click="sendHills = !sendHills"
          >
            <Checkbox
              :binary="true"
              :inputId="'hills-checkbox'"
              :modelValue="sendHills"
              @click.stop
              @update:modelValue="sendHills = $event"
            />
            <label
              class="flex-1 cursor-pointer"
              :for="'hills-checkbox'"
            >
              <span class="font-semibold">{{ $t('cart.label.hillsPetNutrition') }}</span>
              <p class="mt-0.5 text-sm text-slate-500">
                {{ $t('cart.label.hillsPetNutritionDescription') }}
              </p>
            </label>
          </div>

          <div
            v-if="showAdoptAPetOptIn"
            class="flex cursor-pointer items-start gap-3 rounded border border-gray-200 p-3 transition-colors hover:bg-gray-50"
            @click="sendAdoptAPet = !sendAdoptAPet"
          >
            <Checkbox
              :binary="true"
              :inputId="'adopt-a-pet-checkbox'"
              :modelValue="sendAdoptAPet"
              @click.stop
              @update:modelValue="sendAdoptAPet = $event"
            />

            <label
              class="flex-1 cursor-pointer"
              :for="'adopt-a-pet-checkbox'"
            >
              <span class="font-semibold">{{ $t('cart.label.adoptAPetOptIn') }}</span>

              <p class="mt-0.5 text-sm text-slate-500">
                {{ $t('cart.label.adoptAPetOptInDescription') }}
                <a
                  class="text-brand-500 underline"
                  href="https://www.mars.com/privacy"
                  rel="noopener noreferrer"
                  target="_blank"
                  @click.stop
                >
                  {{ $t('cart.label.adoptAPetOptInPrivacyPolicy') }}
                </a>
              </p>
            </label>
          </div>
        </div>
      </div>
    </main>

    <footer class="flex flex-shrink flex-col gap-4">
      <!-- NOTE :: Both errors sit here, below the total and right above the buttons that cause them. -->
      <AFormError :formError />

      <Message
        v-if="requestErrorMessage"
        pt:text="text-sm"
        severity="error"
        @close="requestErrorMessage = ''"
      >
        {{ $t(requestErrorMessage) }}
      </Message>

      <div class="flex justify-between">
        <Button
          outlined
          class="af-button af-button-success-outlined w-fit"
          icon="pi pi-arrow-left"
          :label="$t('common.back')"
          :loading
          @click="emits('onStepUpdate', previousStep)"
        />

        <div class="flex flex-wrap gap-2">
          <Button
            v-if="shelterHasOffsiteAdoption"
            class="af-button ml-auto w-fit"
            icon="pi pi-clock"
            severity="info"
            :disabled="disableOffsiteButton"
            :label="$t('cart.label.offsite')"
            :loading="loading || paymentTypeSaving"
            @click="onNextStep('offsite')"
          />

          <Button
            v-if="showPrePaymentOption"
            class="af-button ml-auto w-fit"
            icon="pi pi-save"
            severity="info"
            :label="$t(preAdoptLabel)"
            :loading="loading || paymentTypeSaving"
            @click="onNextStep('preAdopt')"
          />

          <Button
            class="af-button af-button-success ml-auto"
            iconPos="right"
            :label="$t('cart.label.submitAndComplete')"
            :loading="loading || isSubmitting || paymentTypeSaving"
            @click="onNextStep(null)"
          >
            <template #icon>
              <FaIcon
                class="mr-2"
                :icon="$icons.faPaw"
              />
            </template>
          </Button>
        </div>
      </div>
    </footer>
  </div>
</template>
